/* istanbul ignore file */
// TODO: increase code coverage
import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'

import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { operations as loadingOperations } from 'store/loadingManager'
import {
  selectors as contextSelectors,
  operations as userContextOperations,
} from 'auth/stores/userContext'

import { navigationEvent } from 'layers/navigation/store/operations'

import { DASHBOARD_ROUTE } from 'utils/highschoolHelpers'

import {
  findSiteById,
  getProgramType,
  getSiteFromUrl,
  getDefaultSite,
  getSiteAndUserPreferences,
  isSiteSetup,
  navigate,
  isSiteQueryParamPresent,
  getActiveSiteIdKeyForProgramType,
} from './helpers'

import SiteGuard, { createSiteGuard } from './guard'

import { SITE_QUERY_PARAM, LOADING_TEXT } from './constants'

import {
  isK5DashboardOrK5Program,
  isMiddleSchoolDashboardOrMiddleSchoolProgram,
  isHighSchoolDashboardOrHighSchoolProgram,
} from 'utils/productHelpers'

const PROGRAM_KEY_BY_TYPE = {
  highSchool: 'highSchool',
  middleSchool: 'mspDigital',
  k5: 'k5SelDigital',
}

const DEFAULT_HIGH_SCHOOL_COURSE = {
  name: 'highschool',
  version: null,
}

const getProgramForCurrentSiteContext = ({ programType, programs }) => {
  const programKey = PROGRAM_KEY_BY_TYPE[programType]
  const currentProgram = programKey ? programs?.[programKey] : null

  if (!currentProgram || !programKey) {
    return {}
  }

  if (programType !== 'highSchool') {
    return {
      [programKey]: currentProgram,
    }
  }

  const highSchoolCourses = Array.isArray(currentProgram?.courses)
    ? currentProgram.courses
    : []
  const selectedCourse =
    highSchoolCourses.find(course => course?.name === 'highschool') ||
    highSchoolCourses[0] ||
    DEFAULT_HIGH_SCHOOL_COURSE

  return {
    [programKey]: {
      ...currentProgram,
      courses: [selectedCourse],
    },
  }
}

export default function withSiteContext(Component, options) {
  const { useGuard } = options || {}
  const innerComponent = props => {
    // No early return for K5 path - we need proper initialization
    // even for /dashboard/grades-k-5 to avoid state update errors

    const {
      allSites,
      fetchInstructorData,
      fetchOrCreateInstructorData,
      fetchUserSites,
      k5ActiveSites,
      msActiveSites,
      hsActiveSites,
      instructorData,
      isFetchingSites,
      isFetchingSitesV4,
      isFetchingInstructorData,
      isLoadingApp,
      navigationEvent,
      setElementarySiteContext,
      setMiddleSchoolSiteContext,
      setHighSchoolSiteContext,
      setDigitalWellBeingSiteContext,
      setAppIsLoadingState,
      setAppNotLoadingState,
      userContext,
      v4SitesRaw,
      persistComplete,
    } = props || {}

    const programType = getProgramType()
    const isDigitalWellBeingRoute =
      typeof window !== 'undefined' &&
      window?.location?.pathname?.includes('/digital-well-being')

    // Initialize with null to distinguish between "not fetched" and "fetched but empty"
    let activeSites = null

    // Set the active sites based on program type
    switch (programType) {
      case 'highSchool':
        activeSites = hsActiveSites
        break
      case 'middleSchool':
        activeSites = msActiveSites
        break
      case 'k5':
        activeSites = k5ActiveSites
        break
      case 'dwb':
        activeSites = allSites
        break
      default:
        // For tests, use an empty array instead of null to avoid blocking rendering
        activeSites = []
    }

    // DWB can be licensed outside the traditional K5/MSP program buckets.
    // If we're on a DWB route and the program-specific list is empty, use
    // all active sites so site initialization can still complete.
    if (
      isDigitalWellBeingRoute &&
      Array.isArray(activeSites) &&
      activeSites.length === 0 &&
      Array.isArray(allSites)
    ) {
      activeSites = allSites
    }

    // Component state
    const [currentSite, setCurrentSiteState] = useState(null)
    const [hasInitialized, setHasInitialized] = useState(false)
    const [needsActiveSiteUpdate, setNeedsActiveSiteUpdate] = useState(false)

    // Track sites we've already tried to fetch data for to avoid infinite loops
    const fetchedSitesRef = useRef(new Set())

    // First effect: fetch sites if needed, using Redux fetching flags to avoid loops
    useEffect(() => {
      // Only fetch sites if:
      // 1. We're not already fetching sites
      // 2. We're not already fetching v4 sites
      // 3. AND both legacy-derived activeSites and raw v4 sites are null/undefined (not yet fetched)
      if (
        !isFetchingSites &&
        !isFetchingSitesV4 &&
        allSites === null &&
        (v4SitesRaw === null || v4SitesRaw === undefined)
      ) {
        // Call fetchUserSites to get the sites data
        fetchUserSites()
      }
    }, [
      isFetchingSites,
      isFetchingSitesV4,
      v4SitesRaw,
      fetchUserSites,
      allSites,
    ])

    // Ensure dependencies are stable to prevent infinite loops
    useEffect(() => {
      // Wait for redux persist rehydration to complete before initializing
      if (!persistComplete) {
        return
      }

      // Skip initialization if the component has already been initialized
      if (hasInitialized) {
        return
      }

      // Skip if we're missing essential data
      // Note: activeSites could be null (not fetched yet) or [] (fetched but empty)
      if (
        !programType ||
        isFetchingSites ||
        !activeSites ||
        (activeSites && activeSites.length === 0)
      ) {
        return
      }

      // Get the correct active site ID key for the current program type
      const activeSiteIdKey = getActiveSiteIdKeyForProgramType(programType)

      // Get active site ID for the CURRENT program type using the correct key
      const activeSiteIdFromContext = userContext?.[activeSiteIdKey]

      // First priority: Site from URL query parameter (only if present)
      const siteFromUrl = getSiteFromUrl(activeSites)

      // Second priority: Persisted site from Redux state
      // IMPORTANT FIX: Don't rely on persistComplete for using the active site ID
      // Instead, check if activeSiteIdFromContext exists and is valid
      const siteFromContext = activeSiteIdFromContext
        ? findSiteById(activeSites, activeSiteIdFromContext)
        : null

      // Third priority: Default site (first site alphabetically)
      const defaultSite = getDefaultSite(activeSites)

      // Handle the case when URL has a site parameter but the site is not found
      if (
        !isFetchingSites &&
        isSiteQueryParamPresent() &&
        !siteFromUrl &&
        (siteFromContext || defaultSite)
      ) {
        // TODO: (in future pull request): move this logic to helpers.js
        let dashboardRoute
        switch (programType) {
          case 'highSchool':
            dashboardRoute = '/dashboard/grades-9-12'
            break
          case 'middleSchool':
            dashboardRoute = '/dashboard/grades-6-8'
            break
          case 'k5':
            dashboardRoute = '/dashboard/grades-k-5'
            break
          default:
            dashboardRoute = DASHBOARD_ROUTE
        }

        // Navigate to the correct site (either persisted or default)
        const siteToUse = siteFromContext || defaultSite
        const newUrl = `${dashboardRoute}?${SITE_QUERY_PARAM}=${siteToUse.siteId}`
        navigate(new URL(newUrl, window.location.origin))
        return
      }

      // Choose the current site based on the priorities above
      // URL parameter > persisted site ID > default site
      let newCurrentSite
      if (siteFromUrl) {
        // Priority 1: URL parameter (if present)
        newCurrentSite = siteFromUrl
      } else if (activeSiteIdFromContext && !isFetchingSites) {
        // Priority 2: Use the active site ID from Redux context if available
        // Find the specific site by ID from the available sites
        newCurrentSite = findSiteById(activeSites, activeSiteIdFromContext)
        // If the site wasn't found (perhaps it no longer exists), fall back to default
        if (!newCurrentSite && defaultSite) {
          newCurrentSite = defaultSite
        }
      } else if (siteFromContext) {
        // Alternative way to get persisted site
        newCurrentSite = siteFromContext
      } else {
        // Priority 3: Default site (first site alphabetically)
        newCurrentSite = defaultSite
      }

      if (newCurrentSite) {
        // Safely call our imported functions
        const siteSetup = isSiteSetup(newCurrentSite, programType, userContext)

        const siteWithSetupInfo = {
          ...newCurrentSite,
          isSetup: siteSetup,
        }

        const siteWithPreferences = getSiteAndUserPreferences(
          siteWithSetupInfo,
          allSites,
          programType,
        )

        setCurrentSiteState(siteWithPreferences)

        // CRITICAL FIX: We should update active site in context if:
        // 1. We have a site from URL parameter (always update with URL parameter)
        // 2. OR the URL site differs from the context site (site switching)
        // 3. OR we don't have a site ID in context yet
        // This ensures URL parameters always take precedence over persisted state
        const shouldUpdateActiveSite =
          siteFromUrl ||
          (siteFromUrl &&
            siteFromContext &&
            siteFromUrl.siteId !== siteFromContext.siteId) ||
          (!siteFromUrl && !siteFromContext && activeSiteIdKey && userContext)

        if (shouldUpdateActiveSite) {
          setNeedsActiveSiteUpdate(true)
        }

        setHasInitialized(true)
      }
    }, [
      persistComplete, // Ensure this is stable
      hasInitialized,
      programType,
      isFetchingSites,
      activeSites, // Include the entire activeSites reference to detect null vs []
      currentSite,
      allSites,
      userContext,
    ])

    // Third effect: update active site in context if needed
    useEffect(() => {
      if (needsActiveSiteUpdate && currentSite && currentSite.siteId) {
        setAppIsLoadingState(LOADING_TEXT)

        // Call the appropriate site context setter based on program type
        let siteUpdatePromise
        if (isDigitalWellBeingRoute) {
          siteUpdatePromise = setDigitalWellBeingSiteContext(currentSite.siteId)
        } else {
          switch (programType) {
            case 'k5':
              siteUpdatePromise = setElementarySiteContext(currentSite.siteId)
              break
            case 'middleSchool':
              siteUpdatePromise = setMiddleSchoolSiteContext(currentSite.siteId)
              break
            default:
              // Default to high school or any other program type
              siteUpdatePromise = setHighSchoolSiteContext(currentSite.siteId)
              break
          }
        }

        // Add URL parameter if it's not present and we're setting the site
        // This ensures URL state is consistent with Redux state
        if (
          siteUpdatePromise &&
          siteUpdatePromise.then &&
          !isSiteQueryParamPresent()
        ) {
          siteUpdatePromise
            .then(() => {
              // Only add parameter if we're not already navigating elsewhere
              if (document.visibilityState !== 'hidden') {
                const url = new URL(window.location.href)
                url.searchParams.set(SITE_QUERY_PARAM, currentSite.siteId)
                window.history.replaceState({}, '', url.toString())
              }

              setNeedsActiveSiteUpdate(false)
            })
            .catch(() => {
              setNeedsActiveSiteUpdate(false)
            })
        } else {
          setNeedsActiveSiteUpdate(false)
        }
      }
    }, [
      needsActiveSiteUpdate,
      currentSite,
      programType,
      setElementarySiteContext,
      setMiddleSchoolSiteContext,
      setHighSchoolSiteContext,
      setDigitalWellBeingSiteContext,
      setAppIsLoadingState,
    ])

    // Fourth effect: fetch instructor data if needed
    useEffect(() => {
      if (!programType || !currentSite || !currentSite.siteId) return

      // Check if we've already attempted to fetch data for this site to prevent infinite loops
      const siteIdStr = String(currentSite.siteId)
      if (fetchedSitesRef.current.has(siteIdStr)) {
        return
      }

      // Updated comparison to handle type mismatches
      const hasInstructorData =
        instructorData && String(instructorData?.site?.site_id) === siteIdStr

      // Always fetch instructor data for the current site if we don't have it yet
      const shouldFetchInstructorData =
        !isFetchingInstructorData && !hasInstructorData && hasInitialized

      if (shouldFetchInstructorData) {
        // Add this site to our Set so we don't try to fetch it again
        fetchedSitesRef.current.add(siteIdStr)

        // Prepare proper userContext with ONLY the current site data for the API call
        const enrichedUserContext = {
          // Preserve important user context properties
          ...userContext,
          // Replace the sites array with just the current site
          sites: [],
        }

        // Find the current site in allSites to get full site data
        // Handle case where allSites might be null or undefined in test environment
        const currentSiteData =
          allSites && Array.isArray(allSites)
            ? allSites.find(
                site => String(site.id || site.siteId) === siteIdStr,
              )
            : null

        const currentUserContextSite =
          userContext?.sites?.find(
            site => String(site?.siteId) === String(currentSite.siteId),
          ) || userContext?.sites?.[0]
        const sitePrograms = getProgramForCurrentSiteContext({
          programType,
          programs: currentUserContextSite?.programs,
        })

        // Create a site object with the necessary data
        // Use fallback values for test environment if currentSiteData is null
        const siteForContext = {
          siteId: currentSite.siteId,
          siteName:
            currentSite.siteName ||
            (currentSiteData ? currentSiteData.name : 'Test Site'),
          // Use claims from userContext if available, otherwise default to Teacher for tests
          claims: currentUserContextSite?.claims || ['Teacher'],
          // Ensure we only pass the active program. HS additionally needs one course.
          programs: sitePrograms,
        }

        // Add only this site to the sites array
        enrichedUserContext.sites = [siteForContext]

        // Get the correct key for the active site ID
        const activeSiteIdKey = getActiveSiteIdKeyForProgramType(programType)

        // Update active site ID using the correct key
        enrichedUserContext[activeSiteIdKey] = currentSite.siteId

        if (getProgramType() === 'highSchool') {
          fetchOrCreateInstructorData(currentSite.siteId, enrichedUserContext)
        } else {
          fetchInstructorData()
        }
      }
    }, [
      currentSite,
      instructorData,
      isFetchingInstructorData,
      programType,
      userContext,
      allSites,
      fetchInstructorData,
      hasInitialized,
    ])

    // Fifth effect: manage loading state for both initialization and data fetching
    useEffect(() => {
      const isLoading = isFetchingSites || isFetchingInstructorData

      // Case 1: Set loading state during data fetching
      if (isLoading && !isLoadingApp) {
        setAppIsLoadingState(LOADING_TEXT)
      }
      // Case 2: Set loading state during initialization
      else if (programType && !hasInitialized && !isLoadingApp) {
        setAppIsLoadingState(LOADING_TEXT)
      }
      // Case 3: Clear loading state when data fetching is complete
      else if (!isLoading && isLoadingApp && (!programType || hasInitialized)) {
        setAppNotLoadingState()
      }
      // Case 4: Clear loading state when initialization is complete and we have a site
      else if (isLoadingApp && hasInitialized && currentSite) {
        setAppNotLoadingState()
      }
      return () => setAppNotLoadingState()
    }, [
      isFetchingSites,
      isFetchingInstructorData,
      isLoadingApp,
      programType,
      hasInitialized,
      currentSite,
      setAppIsLoadingState,
      setAppNotLoadingState,
    ])

    function render() {
      if (useGuard && currentSite) {
        return (
          <SiteGuard
            guard={createSiteGuard()}
            navigate={navigate}
            site={currentSite}
          >
            <Component
              {...props}
              activeSites={activeSites}
              currentSite={currentSite}
              instructorData={instructorData}
              navigationEvent={navigationEvent}
              setActiveSite={{
                k5: setElementarySiteContext,
                middleSchool: setMiddleSchoolSiteContext,
                highSchool: setHighSchoolSiteContext,
                digitalWellBeing: setDigitalWellBeingSiteContext,
              }}
            />
          </SiteGuard>
        )
      }

      if (programType) {
        return (
          <Component
            {...props}
            activeSites={activeSites}
            currentSite={currentSite}
            instructorData={instructorData}
            navigationEvent={navigationEvent}
            setActiveSite={{
              k5: setElementarySiteContext,
              middleSchool: setMiddleSchoolSiteContext,
              highSchool: setHighSchoolSiteContext,
              digitalWellBeing: setDigitalWellBeingSiteContext,
            }}
          />
        )
      }

      return <Component {...props} />
    }

    // Early return for loading state - but don't block K5 rendering when we're on the K5 dashboard
    if (programType && !hasInitialized && programType === 'highSchool') {
      return null
    }

    return render()
  }

  innerComponent.displayName = 'withSiteContext'

  const mapStateToProps = state => {
    const hsProgramCode = 'HSP'
    const activeSites = licenseSelectors?.selectActiveSites(state) || null
    // Initialize with null to distinguish between "not fetched" and "fetched but empty"
    let k5ActiveSites = null
    let msActiveSites = null
    let hsActiveSites = null

    // Only process activeSites if it's an array (not null)
    if (Array.isArray(activeSites)) {
      // If activeSites is an array (even empty), set program-specific sites to arrays
      k5ActiveSites = activeSites.filter(
        s =>
          s?.licenses?.some(
            l => l?.product?.programCodes?.includes('K5P') && !l?.isExpired,
          ) &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin')),
      )
      msActiveSites = activeSites.filter(
        s =>
          s?.licenses?.some(
            l => l?.product?.programCodes?.includes('MSP') && !l?.isExpired,
          ) &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin')),
      )
      hsActiveSites = activeSites.filter(
        s =>
          s?.licenses?.some(
            l =>
              l?.product?.programCodes?.includes(hsProgramCode) &&
              !l?.isExpired,
          ) &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin')),
      )
    }

    const isk5 = isK5DashboardOrK5Program()
    const isMiddleSchool = isMiddleSchoolDashboardOrMiddleSchoolProgram()
    const isHighSchool = isHighSchoolDashboardOrHighSchoolProgram()

    let instructorData
    switch (true) {
      case isk5:
        instructorData = lmsSelectors.selectk5InstructorData(state)
        break
      case isMiddleSchool:
        instructorData = lmsSelectors.selectMiddleSchoolInstructorData(state)
        break
      case isHighSchool:
        instructorData = lmsSelectors.selectHsInstructorData(state)
        break
      default:
        instructorData = lmsSelectors.selectHsInstructorData(state)
    }

    return {
      allSites: activeSites,
      k5ActiveSites,
      msActiveSites,
      hsActiveSites,
      // v4 fetching status and data to gate fetches safely
      isFetchingSitesV4: state.ssapiManager?.isFetchingUserSites,
      v4SitesRaw: state.ssapiManager?.sites,
      // TODO LEARN-20166 Update hs to use useUserCOntext

      userContext: contextSelectors.selectProgramContextWithSiteSelection(
        state,
      ),
      instructorData: instructorData,
      isFetchingInstructorData:
        state.lmsManager.isFetchingOrCreatingProgramFlex,
      isFetchingSites: state.licenseManager.isFetchingUserSites,
      isLoadingApp: state.loadingManager?.isLoading,
      // Use our dedicated persistStatus reducer instead of relying on _persist property
      persistComplete: state.persistStatus?.rehydrated === true,
    }
  }

  const mapDispatchToProps = {
    fetchUserSites: licenseOperations.fetchUserSites,
    fetchInstructorData: lmsOperations.fetchInstructorProgramFlexGet,
    fetchOrCreateInstructorData:
      lmsOperations.fetchOrCreateInstructorProgramFlex,
    setHighSchoolSiteContext: userContextOperations.setHighSchoolSiteContext,
    setElementarySiteContext: userContextOperations.setElementarySiteContext,
    setMiddleSchoolSiteContext:
      userContextOperations.setMiddleSchoolSiteContext,
    setDigitalWellBeingSiteContext:
      userContextOperations.setDigitalWellBeingSiteContext,
    navigationEvent: navigationEvent,
    setAppIsLoadingState: loadingOperations.setIsLoadingState,
    setAppNotLoadingState: loadingOperations.setNotLoadingState,
  }

  return connect(mapStateToProps, mapDispatchToProps)(innerComponent)
}
